import React, { useState, useEffect } from 'react';
import { X, Upload, Trash2, Calendar, AlertCircle } from 'lucide-react';
import { employeesAPI } from '../../../api/employees';
import { useCompany } from '../../../contexts/CompanyContext';
import { getEmployeeBalance } from '../../../api/vacations';
import { usePermissionsContext } from '../../../contexts/PermissionsContext';

export default function AbsenceForm({ absence, types, currentUser, onSubmit, onClose }) {
  const { currentCompany } = useCompany();
  const { can } = usePermissionsContext();
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [vacBalance, setVacBalance] = useState(null); // balance cuando tipo=vacaciones

  const [formData, setFormData] = useState({
    employee_id: currentUser?.employee_id || '',
    absence_type_id: '',
    fecha_inicio: '',
    fecha_fin: '',
    motivo: '',
    suplente_id: '',
    instrucciones_suplente: ''
  });

  const [documentos, setDocumentos] = useState([]);
  const [errors, setErrors] = useState({});

  // Cargar empleados si es admin
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        console.log('AbsenceForm - Cargando empleados...');
        const response = await employeesAPI.getAll({ limit: 1000, estado: 'activo' });
        console.log('AbsenceForm - Response de empleados:', response);
        console.log('AbsenceForm - response.success:', response.success);
        console.log('AbsenceForm - response.employees:', response.employees);
        if (response.success) {
          console.log('AbsenceForm - Empleados cargados:', response.employees.length);
          setEmployees(response.employees);
        } else {
          console.error('AbsenceForm - Response sin success flag');
        }
      } catch (err) {
        console.error('AbsenceForm - Error loading employees:', err);
      }
    };

    if (can('VACATION_APPROVE')) {
      loadEmployees();
    }
  }, [currentUser]);

  // Actualizar tipo seleccionado cuando cambia
  useEffect(() => {
    if (formData.absence_type_id) {
      const type = types.find((t) => t.id === formData.absence_type_id);
      setSelectedType(type);
    } else {
      setSelectedType(null);
    }
  }, [formData.absence_type_id, types]);

  // Cargar balance de vacaciones cuando el tipo es de categoría vacaciones
  useEffect(() => {
    if (selectedType?.categoria !== 'vacaciones' || !formData.employee_id) {
      setVacBalance(null);
      return;
    }
    const year = formData.fecha_inicio
      ? new Date(formData.fecha_inicio).getFullYear()
      : new Date().getFullYear();
    getEmployeeBalance(formData.employee_id, year)
      .then(res => setVacBalance(res.data ?? null))
      .catch(() => setVacBalance(null));
  }, [selectedType, formData.employee_id, formData.fecha_inicio]);

  // Si está editando, cargar datos
  useEffect(() => {
    if (absence) {
      setFormData({
        employee_id: absence.employee_id,
        absence_type_id: absence.absence_type_id,
        fecha_inicio: absence.fecha_inicio,
        fecha_fin: absence.fecha_fin,
        motivo: absence.motivo || '',
        suplente_id: absence.suplente_id || '',
        instrucciones_suplente: absence.instrucciones_suplente || ''
      });
    }
  }, [absence]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Limpiar error del campo
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setDocumentos((prev) => [...prev, ...files]);
  };

  const removeDocument = (index) => {
    setDocumentos((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.employee_id) newErrors.employee_id = 'Seleccione un empleado';
    if (!formData.absence_type_id) newErrors.absence_type_id = 'Seleccione un tipo de ausencia';
    if (!formData.fecha_inicio) newErrors.fecha_inicio = 'Ingrese la fecha de inicio';
    if (!formData.fecha_fin) newErrors.fecha_fin = 'Ingrese la fecha de fin';

    // Validar que fecha_fin >= fecha_inicio
    if (formData.fecha_inicio && formData.fecha_fin) {
      if (new Date(formData.fecha_fin) < new Date(formData.fecha_inicio)) {
        newErrors.fecha_fin = 'La fecha de fin debe ser posterior a la fecha de inicio';
      }
    }

    // Si el tipo requiere documento, validar
    if (selectedType?.requiere_documento && documentos.length === 0 && !absence) {
      newErrors.documentos = 'Este tipo de ausencia requiere al menos un documento adjunto';
    }

    // Validar días de anticipación mínima
    if (selectedType?.dias_anticipacion_minima && formData.fecha_inicio) {
      const diasAnticipacion = Math.ceil(
        (new Date(formData.fecha_inicio) - new Date()) / (1000 * 60 * 60 * 24)
      );
      if (diasAnticipacion < selectedType.dias_anticipacion_minima) {
        newErrors.fecha_inicio = `Debe solicitar con al menos ${selectedType.dias_anticipacion_minima} días de anticipación`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      const result = await onSubmit(formData, documentos);
      if (result.success) {
        onClose();
      } else {
        setErrors({ submit: result.error || 'Error al crear la solicitud' });
      }
    } catch (err) {
      setErrors({ submit: err.message || 'Error inesperado' });
    } finally {
      setLoading(false);
    }
  };

  const canSelectEmployee = can('VACATION_APPROVE');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-pink-500 text-white p-6 rounded-t-2xl flex items-center justify-between">
          <h2 className="text-2xl font-bold">
            {absence ? 'Editar Ausencia' : 'Nueva Solicitud de Ausencia'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error general */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-700">{errors.submit}</p>
            </div>
          )}

          {/* Empleado */}
          {canSelectEmployee ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Empleado <span className="text-red-500">*</span>
              </label>
              <select
                name="employee_id"
                value={formData.employee_id}
                onChange={handleChange}
                className={`w-full px-4 py-2 border ${
                  errors.employee_id ? 'border-red-300' : 'border-gray-300'
                } rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent`}
              >
                <option value="">Seleccione un empleado</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.nombre} {emp.apellido} ({emp.employeeId || emp.employee_id || ''})
                  </option>
                ))}
              </select>
              {errors.employee_id && (
                <p className="text-red-500 text-sm mt-1">{errors.employee_id}</p>
              )}
            </div>
          ) : (
            <input type="hidden" name="employee_id" value={formData.employee_id} />
          )}

          {/* Tipo de Ausencia */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Ausencia <span className="text-red-500">*</span>
            </label>
            <select
              name="absence_type_id"
              value={formData.absence_type_id}
              onChange={handleChange}
              className={`w-full px-4 py-2 border ${
                errors.absence_type_id ? 'border-red-300' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent`}
            >
              <option value="">Seleccione un tipo</option>
              {types.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.nombre}
                  {type.requiere_documento && ' (requiere documento)'}
                </option>
              ))}
            </select>
            {errors.absence_type_id && (
              <p className="text-red-500 text-sm mt-1">{errors.absence_type_id}</p>
            )}
            {selectedType && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                <p className="font-semibold mb-1">Información del tipo:</p>
                <ul className="list-disc list-inside space-y-1">
                  {selectedType.es_remunerado ? (
                    <li>Remunerado ({selectedType.porcentaje_pago}% del salario)</li>
                  ) : (
                    <li>No remunerado</li>
                  )}
                  {selectedType.dias_anticipacion_minima > 0 && (
                    <li>Anticipación mínima: {selectedType.dias_anticipacion_minima} días</li>
                  )}
                  {selectedType.dias_maximos_por_solicitud && (
                    <li>Máximo por solicitud: {selectedType.dias_maximos_por_solicitud} días</li>
                  )}
                  {selectedType.requiere_documento && <li>Requiere documento adjunto</li>}
                </ul>
              </div>
            )}

            {/* Balance de vacaciones disponible */}
            {selectedType?.categoria === 'vacaciones' && formData.employee_id && (
              <div className={`mt-2 p-3 rounded-lg border text-sm ${
                vacBalance === null
                  ? 'bg-gray-50 border-gray-200 text-gray-500'
                  : (vacBalance.days_available ?? 0) > 0
                    ? 'bg-teal-50 border-teal-200 text-teal-800'
                    : 'bg-red-50 border-red-200 text-red-700'
              }`}>
                {vacBalance === null ? (
                  <span>Cargando balance de vacaciones…</span>
                ) : (
                  <div className="flex flex-wrap gap-4">
                    <span><strong>Asignados:</strong> {vacBalance.days_allocated ?? 0} días</span>
                    <span><strong>Usados:</strong> {vacBalance.days_used ?? 0} días</span>
                    <span><strong>Disponibles:</strong> {vacBalance.days_available ?? 0} días</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Fechas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha de Inicio <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="fecha_inicio"
                value={formData.fecha_inicio}
                onChange={handleChange}
                className={`w-full px-4 py-2 border ${
                  errors.fecha_inicio ? 'border-red-300' : 'border-gray-300'
                } rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent`}
              />
              {errors.fecha_inicio && (
                <p className="text-red-500 text-sm mt-1">{errors.fecha_inicio}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha de Fin <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="fecha_fin"
                value={formData.fecha_fin}
                onChange={handleChange}
                className={`w-full px-4 py-2 border ${
                  errors.fecha_fin ? 'border-red-300' : 'border-gray-300'
                } rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent`}
              />
              {errors.fecha_fin && (
                <p className="text-red-500 text-sm mt-1">{errors.fecha_fin}</p>
              )}
            </div>
          </div>

          {/* Motivo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motivo
            </label>
            <textarea
              name="motivo"
              value={formData.motivo}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Describa brevemente el motivo de la ausencia..."
            />
          </div>

          {/* Suplente (opcional) */}
          {canSelectEmployee && employees.length > 0 && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Suplente (opcional)
                </label>
                <select
                  name="suplente_id"
                  value={formData.suplente_id}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="">Sin suplente</option>
                  {employees
                    .filter((emp) => emp.id !== formData.employee_id)
                    .map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.nombre} {emp.apellido}
                      </option>
                    ))}
                </select>
              </div>

              {formData.suplente_id && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Instrucciones para el suplente
                  </label>
                  <textarea
                    name="instrucciones_suplente"
                    value={formData.instrucciones_suplente}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Instrucciones o notas para el suplente..."
                  />
                </div>
              )}
            </>
          )}

          {/* Documentos */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Documentos Adjuntos
              {selectedType?.requiere_documento && (
                <span className="text-red-500">*</span>
              )}
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              <input
                type="file"
                id="file-upload"
                multiple
                onChange={handleFileChange}
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              />
              <label
                htmlFor="file-upload"
                className="flex flex-col items-center cursor-pointer"
              >
                <Upload className="w-10 h-10 text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">
                  Click para subir archivos o arrastre aquí
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  PDF, JPG, PNG, DOC (máx. 10MB)
                </span>
              </label>
            </div>

            {/* Lista de archivos */}
            {documentos.length > 0 && (
              <div className="mt-3 space-y-2">
                {documentos.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <span className="text-sm text-gray-700 truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeDocument(index)}
                      className="text-red-600 hover:bg-red-50 p-1 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {errors.documentos && (
              <p className="text-red-500 text-sm mt-1">{errors.documentos}</p>
            )}
          </div>

          {/* Botones */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Enviando...' : absence ? 'Actualizar' : 'Enviar Solicitud'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
